import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from 'src/common/utils/api';
import { FileStatus, FileUploadInput, UploadedFile } from './types';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';
import { useUploadToStorage } from './useUploadToStorage';
import { uploadPool, UPLOAD_CONCURRENCY } from './uploadPool';
import { invalidateDataUploadFiles } from './invalidateDataUploadFiles';

type FileUploadRequest = {
  fileName?: string;
  fileSize: number;
  fileType: string;
  isFolder: boolean;
  isResolved: boolean;
  isTemporary: boolean;
  modelId?: string;
  parentId?: string;
  documentDate?: string | null;
  location?: string;
  uploadToken?: string;
};

export const useFileUploadActions = ({
  clientId: overrideClientId,
}: { clientId?: string } = {}) => {
  const { clientId: defaultClientId, clients } = useCurrentClientIfAvailable();
  const clientId = overrideClientId ?? defaultClientId;
  const client = clients.find(c => c.id === clientId);
  const familyId = client?.family?.id ?? client?.familyId;
  const queryClient = useQueryClient();
  const { uploadToStorage, extractMetadata } = useUploadToStorage();
  const resource = `clients/${clientId}/data-upload-files`;
  const invalidateFiles = () => invalidateDataUploadFiles(queryClient, { clientId, familyId });
  const [uploadProgress, setUploadProgress] = useState({ completed: 0, total: 0 });

  const {
    mutateAsync: deleteFileMutation,
    error: deleteError,
    reset: resetDeleteError,
    isPending: isDeleting,
  } = useMutation<unknown, Error, string>({
    mutationFn: (fileId: string) => api.delete(`${resource}/${fileId}`),
    onSuccess: () => {
      return invalidateFiles();
    },
  });

  const uploadSingleFile = async (fileInput: FileUploadInput): Promise<{ data: UploadedFile }> => {
    const requestInput: FileUploadRequest = {
      fileName: fileInput.fileName || fileInput.file?.name,
      fileSize: fileInput.file?.size || 0,
      fileType: fileInput.file?.type || 'FOLDER',
      isFolder: fileInput.isFolder || false,
      isResolved: fileInput.isResolved || false,
      isTemporary: fileInput.isTemporary || false,
      modelId: fileInput.modelId || undefined,
      parentId: fileInput.parentId,
      documentDate: fileInput.documentDate?.split('T')[0],
    };
    if (fileInput.file) {
      const uploadResult = await uploadToStorage(fileInput.file, clientId);
      if (!requestInput.documentDate) {
        const metadata = await extractMetadata(
          uploadResult.location,
          fileInput.file,
          uploadResult.uploadToken,
        );
        if (metadata.creationDate) {
          requestInput.documentDate = metadata.creationDate;
        }
      }
      if (uploadResult.location) {
        requestInput.location = uploadResult.location;
        requestInput.uploadToken = uploadResult.uploadToken;
      }
    }
    return api.post<UploadedFile>(resource, requestInput);
  };

  const { mutateAsync: uploadFiles, error: uploadError } = useMutation<
    { data: UploadedFile }[],
    Error,
    FileUploadInput[]
  >({
    mutationFn: (files: FileUploadInput[]) => {
      setUploadProgress({ completed: 0, total: files.length });
      return uploadPool(
        files.map(fileInput => ({
          task: () => uploadSingleFile(fileInput),
          label: fileInput.file?.name ?? fileInput.fileName ?? 'Unknown file',
        })),
        {
          concurrency: UPLOAD_CONCURRENCY,
          context: { clientId },
          onSettled: () => setUploadProgress(p => ({ ...p, completed: p.completed + 1 })),
        },
      );
    },
    onSuccess: () => {
      return invalidateFiles();
    },
  });

  const deleteFile = async (fileId: string) => {
    await deleteFileMutation(fileId);
  };

  const setFileResolved = async (file: UploadedFile, isResolved: boolean) => {
    await api.put(`${resource}/${file.id}`, {
      isResolved,
      isFiled: false,
      parentId: file.parentId ?? null,
      fileName: null,
      documentDate: file.documentDate ?? null,
    });
    await invalidateFiles();
  };

  const moveFile = async (file: UploadedFile, parentId: string | null | undefined) => {
    await api.put(`${resource}/${file.id}`, {
      parentId: parentId ?? null,
      isResolved: file.isResolved,
      isFiled: true,
      fileName: null,
      documentDate: file.documentDate ?? null,
    });
    await invalidateFiles();
  };

  const createFolder = async (name: string, parentId?: string) => {
    await api.post(resource, {
      fileName: name,
      fileSize: 0,
      fileType: 'FOLDER',
      isFolder: true,
      isResolved: true,
      parentId,
    });
    await invalidateFiles();
  };

  const renameFolder = async (file: UploadedFile, newName: string) => {
    await api.put(`${resource}/${file.id}`, {
      isResolved: file.isResolved,
      isFiled: false,
      parentId: file.parentId ?? null,
      fileName: newName,
    });
    await invalidateFiles();
  };

  const getFileStatus = (file: UploadedFile): FileStatus => {
    const status = file.backgroundProcess?.status;
    if (status === 'SUCCESS' || !status) {
      return { color: 'green.6', label: 'Ready' };
    } else if (status === 'FAILED') {
      return { color: 'red.6', label: 'Failed' };
    } else {
      return {
        color: 'blue.6',
        label: 'Processing',
        showLoader: true,
      };
    }
  };

  return {
    uploadFiles,
    uploadProgress,
    deleteFile,
    deleteError,
    resetDeleteError,
    isDeleting,
    setFileResolved,
    moveFile,
    createFolder,
    renameFolder,
    getFileStatus,
    error: uploadError,
  };
};
